import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileDown, Check, Loader2 } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { useToast } from "@/hooks/useToast";
import { serializeLcSettings } from "@/utils/lcSettingsToml";
import { isTvExportable, mergeForExport } from "@/utils/featureParams";

const UNSUPPORTED_COUNT_NOTICE =
  "The TradingView bridge currently supports 5 feature slots; this study uses a different count.";

interface ExportTomlButtonProps {
  bestParams: Record<string, unknown>;
  fixedValues: Record<string, unknown>;
  studyName?: string;
  disabled?: boolean;
}

function buildExportFilename(name: string): string {
  const sanitized = name
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 100);

  const now = new Date();
  const stamp =
    now.getFullYear().toString() +
    String(now.getMonth() + 1).padStart(2, "0") +
    String(now.getDate()).padStart(2, "0") +
    "-" +
    String(now.getHours()).padStart(2, "0") +
    String(now.getMinutes()).padStart(2, "0");

  return `opt-settings-${stamp}-${sanitized}.toml`;
}

export function ExportTomlButton({
  bestParams,
  fixedValues,
  studyName = "study",
  disabled,
}: ExportTomlButtonProps) {
  const api = useApi();
  const { toast } = useToast();
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  const [defaults, setDefaults] = useState<Record<string, unknown> | null>(
    null,
  );

  // AIE-534: the LC indicator bridge serializes exactly 5 slots; block export for any other arity.
  const exportable = isTvExportable(fixedValues);

  const handleExport = async () => {
    setStatus("loading");
    try {
      let lcDefaults = defaults;
      if (!lcDefaults) {
        lcDefaults = await api.tvBridgeGetDefaults();
        setDefaults(lcDefaults);
      }

      // Shared merge+filter (defaults <- fixed/pinned values <- best_params, filtered to LC keys)
      // so this button stays byte-identical to ExportForTvButton.
      const filtered = mergeForExport(lcDefaults, fixedValues, bestParams);

      const tomlContent = serializeLcSettings(filtered);
      const blob = new Blob([tomlContent], { type: "application/toml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildExportFilename(studyName);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatus("success");
      toast({
        title: "LC settings exported",
        description:
          "TOML file downloaded. Import it into TradingView or a new study.",
      });
      setTimeout(() => setStatus("idle"), 5000);
    } catch {
      setStatus("idle");
      toast({
        title: "Export failed",
        description: "Could not export LC settings. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={disabled || status === "loading" || !exportable}
        title={exportable ? undefined : UNSUPPORTED_COUNT_NOTICE}
      >
        {status === "loading" && (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        )}
        {status === "success" && <Check className="h-4 w-4 mr-2" />}
        {status === "idle" && <FileDown className="h-4 w-4 mr-2" />}
        {status === "success" ? "Exported" : "Export LC Settings"}
      </Button>
      {!exportable && (
        <p className="text-xs text-muted-foreground">
          {UNSUPPORTED_COUNT_NOTICE}
        </p>
      )}
    </div>
  );
}
